import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import Plugin, { PluginProps } from '../Plugin';
import { toRange } from 'xpath-range';
import { onHighlightAction } from '../../../helpers/selection';
import { render } from 'react-dom';
import { tippy } from '@tippyjs/react';
import { hideAll } from 'tippy.js';
import Dropdown, { ColorOption } from '../../Dropdown';
import { PluginType } from '../../../types/JsonCmsContent';
import { useIsMobile } from '../../../../../hooks/isMobile';
import { useIsTablet } from '../../../../../hooks/isTablet';

const getMarkElement = (markId: string) => {
  const markEl = document.querySelector(`#${markId}`) as any;
  return markEl;
};

const Text = (props: PluginProps) => {
  const {
    content,
    notes: allNotes,
    // showExperimentalFeatures,
    createNote,
    updateNoteData,
    // deleteNote,
    showNoteTaking,
    onComment,
  } = props;

  const [contentData, setContentData] = useState('');
  const ref = useRef();
  const [counter, setCounter] = useState(0);
  const rerender = () => setCounter((c) => c + 1);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const addNote = (note: any) => {
    renderNote(note);
  };

  useEffect(() => {
    if (!showNoteTaking) {
      rerender();
      return;
    }
    setTimeout(() => {
      renderAllNotes();
    }, 0);
  }, [allNotes, content, showNoteTaking]);

  const renderAllNotes = () => {
    allNotes.forEach((note, index) => {
      setTimeout(() => {
        renderNote(note);
      }, 0);
    });
  };

  const renderNote = (note: any) => {
    if (
      note?.startContainer &&
      note?.endContainer &&
      note?.startOffset >= 0 &&
      note?.endOffset >= 0
    ) {
      try {
        hideAll();
        let range = toRange(
          note.startContainer,
          note.startOffset,
          note.endContainer,
          note.endOffset,
          ref.current,
        );
        if (range.toString() !== note.text) {
          return;
        }
        const markEl = document.createElement('mark');
        const markId = `mark-${
          note.id ? note.id : Math.random().toString().substring(3)
        }`;
        markEl.id = markId;
        const dropdownEl = document.createElement('div');
        const dropdownId = `dropdown-${Math.random().toString().substring(3)}`;
        dropdownEl.id = dropdownId;
        markEl.appendChild(range.extractContents());
        range.insertNode(markEl);

        if (note.color === ColorOption.transparent) {
          markEl.style.backgroundColor = 'transparent';
          // Deleted note
          return;
        }

        const isColorSet = note.color !== ColorOption.neutral;

        const tippyInstance = tippy(`#${markId}`, {
          content: dropdownEl,
          allowHTML: true,
          showOnCreate: note.id ? false : true,
          trigger: 'mouseenter',
          onShow(instance) {
            hideAll();
            instance.setProps({ trigger: 'click' });
          },
          onHide(instance) {
            instance.setProps({ trigger: 'mouseenter' });
            if (!isColorSet) {
              setTimeout(() => {
                const markEl = getMarkElement(markId);
                if (markEl) {
                  const newColor = markEl.style.background;
                  if (
                    newColor === 'rgb(208, 233, 255)' ||
                    newColor === '#d0e9ff'
                  ) {
                    rerender();
                    renderAllNotes();
                  }
                }
              }, 200);
            }
          },
        });

        const noteData = {
          text: note.text,
          color: note.color,
          startOffset: note.startOffset,
          endOffset: note.endOffset,
          startContainer: note.startContainer,
          endContainer: note.endContainer,
        };
        render(
          <Dropdown
            markId={markId}
            {...note}
            updateColor={(color: ColorOption) => {
              if (!note.id) {
                createNote({ ...noteData, color });
                rerender();
              } else {
                updateNoteData(note.id, { ...noteData, color });
              }
            }}
            deleteNote={
              note.id
                ? () => {
                    updateNoteData(note.id, {
                      ...noteData,
                      color: ColorOption.transparent,
                    });
                    tippyInstance[0]!.destroy();
                  }
                : null
            }
            onComment={note.id ? () => onComment(note.id) : null}
          />,
          dropdownEl,
        );
      } catch (e) {}
    }
  };

  const onMouseUp = () => {
    if (!showNoteTaking) {
      return;
    }
    let el = ref.current;
    onHighlightAction(ColorOption.neutral, addNote, el);
  };

  useEffect(() => {
    // Handle images in text
    let tmpContentData = content.data;
    tmpContentData = tmpContentData.replace('cms-plugin', 'img');
    content.children.forEach((plugin) => {
      if (plugin.plugin === PluginType.PicturePlugin && plugin.data.pk) {
        const pluginIndex = tmpContentData.search(`id="${plugin.data.pk}"`);
        if (pluginIndex !== -1) {
          tmpContentData =
            tmpContentData.slice(0, pluginIndex) +
            `src='${plugin.data.src}'` +
            tmpContentData.slice(pluginIndex);
        }
      }
    });
    setContentData(tmpContentData);
  }, [content.data]);

  return (
    <>
      <Box
        ref={ref as any}
        key={counter}
        onMouseUp={onMouseUp}
        fontFamily='"Open Sans", sans-serif'
        fontSize={isMobile || isTablet ? '14px' : '1rem'}
        textAlign="justify"
        dangerouslySetInnerHTML={{
          __html: contentData,
        }}
        style={{
          whiteSpace: 'initial',
          padding: '5px',
        }}
      ></Box>
      {content.children.map((child, index) => {
        if (child.plugin === PluginType.PicturePlugin) {
          return null;
        }
        return <Plugin key={index} {...props} content={child} />;
      })}
    </>
  );
};

export default Text;
